<template>
    <landlords-current
        v-slot="{
            data: landlord
        }"
    >
        <div class="relative z-0 min-h-full pb-12 overflow-hidden text-grey-darkest">
            <div class="absolute inset-x-0 top-0 bg-blue h-1-5" />

            <job-sheets-find
                :params="{
                    job_sheet: $route.params.job_sheet
                }"
                :include="[
                    'contacts',
                    'entries(include:files(filters:with_erased=false))',
                    'note',
                    'maintenance(include:files(filters:with_erased=false),property(include:landlord))',
                ]"
                v-slot="{
                    data: job_sheet,
                    has_loaded,
                    error,
                    find
                }"
                @success="onJobSheetsFind"
            >
                <div v-if="!has_loaded">
                    <base-loading
                        :retry="find"
                        :error="error"
                        class="py-6"
                    />

                    <error-modal
                        v-if="error && $error.is(error, { exclude: [400, 422] })"
                        :error="error"
                    />
                </div>

                <with-variables
                    v-else
                    :variables="{
                        contact: _.get(job_sheet, 'contacts.data[0]')
                    }"
                    v-slot="{ contact }"
                >
                    <div>
                        <div class="container relative max-w-lg mx-auto">
                            <div class="max-w-lg pt-8 pb-6 mx-auto text-center">
                                <laroute-link
                                    :to="{
                                        name: 'www.home.index'
                                    }"
                                    class="inline-block h-5"
                                >
                                    <base-svg
                                        name="logo-rentora"
                                        class="h-full fill-primary-blue"
                                    />
                                </laroute-link>
                            </div>
                        </div>

                        <div
                            v-if="landlord && landlord.uuid === _.get(job_sheet, 'maintenance.property.landlord.uuid')"
                            class="py-6 mb-6 border-t border-b bg-blue-bg"
                        >
                            <div class="container relative max-w-lg mx-auto">
                                <base-hint name="landlords-job-sheets-show">
                                    <template slot="heading">
                                        Job sheets
                                    </template>

                                    <p>
                                        ...
                                    </p>
                                </base-hint>

                                <header class="flex items-center h-16 mb-6 border-b">
                                    <h1 class="flex-grow text-xl font-semibold">
                                        {{ _.possession(contact.name) }} Job Sheet
                                    </h1>

                                    <div class="flex-none w-6 h-6 mr-3">
                                        <base-tooltip>
                                            <template v-slot:trigger>
                                                <base-svg
                                                    name="icon-view-visible"
                                                    class="w-full h-full fill-primary-grey fill-secondary-grey-darkest"
                                                />
                                            </template>

                                            <template v-slot:tooltip>
                                                This section is only visible to you.
                                            </template>
                                        </base-tooltip>
                                    </div>
                                </header>

                                <div class="mb-6 bg-white rounded shadow-md">
                                    <header class="flex items-center h-16 px-4 border-b rounded-t sm:px-6">
                                        <h1 class="flex-grow text-base font-normal">
                                            Share this job sheet
                                        </h1>
                                    </header>

                                    <div class="flex-grow px-4 py-4 text-grey-darker sm:px-6">
                                        <p class="mb-3">
                                            If your contact has a valid email address and/or telephone number, we've send them this link to this job sheet already.
                                            If you wish to personally send them a link too, share this:
                                        </p>

                                        <form-item
                                            name="link"
                                            class="mb-0!"
                                        >
                                            <template slot="label">Link to job sheet</template>

                                            <div class="flex">
                                                <input-clipboard
                                                    name="link"
                                                    :value="job_sheet.links.www.show"
                                                />
                                            </div>

                                            <template slot="hint">Share this link with the contractor</template>
                                        </form-item>
                                    </div>
                                </div>

                                <notes-card
                                    :note="job_sheet.note"
                                    :notable="job_sheet"
                                    class="shadow-md"
                                />
                            </div>
                        </div>

                        <div class="container relative max-w-lg mx-auto">
                            <div class="mb-0 text-4xl font-semibold leading-tight md:text-5xl text-grey-darkest">
                                {{ job_sheet.maintenance.title }}
                            </div>

                            <base-address
                                :address="job_sheet.maintenance.property.address"
                                :is-inline="true"
                                class="mb-4 text-sm font-semibold sm:text-base text-grey-darker"
                            />

                            <expandable-text
                                :text="job_sheet.maintenance.description"
                                :length="300"
                                class="mb-4 text-grey-darker"
                                placeholder="No description."
                            />

                            <files-lists
                                :files="job_sheet.maintenance.files.data"
                                :show-empty="false"
                            />

                            <h2 class="mt-12 mb-2 text-xl font-semibold text-grey-darkest">
                                Timeline
                            </h2>

                            <job-sheet-entries-list
                                :job-sheet="job_sheet"
                                :entries="job_sheet.entries.data"
                            />

                            <the-comment-form
                                :job-sheet="job_sheet"
                                @success="find"
                                class="-mt-2 border-2 rounded"
                            />

                            <h2 class="mt-12 mb-2 text-xl font-semibold text-grey-darkest">
                                FAQs
                            </h2>

                            <p>Some FAQs here.</p>
                        </div>
                    </div>
                </with-variables>
            </job-sheets-find>
        </div>
    </landlords-current>
</template>

<script>
    import HeadMixin from '../../mixins/head'
    import PreloadedMixin from '../../mixins/preloaded'
    import FilesLists from '../../components/files-lists'
    import JobSheetEntriesList from '../../components/job-sheet-entries-list'
    import JobSheetsFind from '../../components/job-sheets-find'
    import LandlordsCurrent from '../../components/landlords-current'
    import NotesCard from '../../components/notes-card'
    import TheCommentForm from './show/the-comment-form'

    export default {
        mixins: [
            HeadMixin,
            PreloadedMixin
        ],
        components: {
            FilesLists,
            JobSheetEntriesList,
            JobSheetsFind,
            LandlordsCurrent,
            NotesCard,
            TheCommentForm
        },
        data () {
            return {
                store: {
                    job_sheet: null
                }
            }
        },
        computed: {
            title () {
                return this.store.job_sheet
                    ? this.store.job_sheet.maintenance.title
                    : '...'
            }
        },
        methods: {
            onJobSheetsFind ({ data }) {
                this.store.job_sheet = data
            },
            head () {
                return {
                    title: [
                        this.title,
                        'Job Sheet'
                    ]
                }
            }
        }
    }
</script>
